import DynamicForm from "@mavichpe/dynamic-forms";
import { SignInInput } from 'aws-amplify/auth';
import { useEffect, useState } from "react";
import LoginFormConfig from "./form";
import NewPasswordFormConfig from "./newPasswordform";
import { useAuth } from "hooks/useAuth";
import { Container } from "reactstrap";
import { useAppDispatch } from "store/hooks";
import { login } from "store/slices/userSlides";
import { useNavigate } from "react-router-dom";
import styles from "./style.module.scss";
import { fetchSuscription } from "@/api/SuscriptionsApi";
import { setSuscriptionData, setTenantData, setPlanFeatures, setPlanDocumentLimits } from "@/store/slices/suscriptionSlides";
import { SuscriptionTypeApi } from "@/api/SuscriptionTypeApi";
import { parsePlanFeatures } from "@/security/features.catalog";
import { parseDocumentLimits } from "@/security/documentLimits.catalog";
import { ClientApi } from "@/api/ClientApi";
import { CurrencyApi } from "@/api/CurrencyApi";
import { PriceApi } from "@/api/PricesApi";
import { SalemanApi } from "@/api/SalemenApi";
import { ActivityCodeApi } from "@/api/ActivityCodeApi";
import { BankAccountApi } from "@/api/BankAccountApi";
import { CustomFieldApi } from "@/api/CustomFieldsApi";
import { InvoiceTemplateApi } from "@/api/InvoiceTemplateApi";
import { ZoneApi } from "@/api/ZoneApi";
import { CategoryApi } from "@/api/CategoryApi";
import { clearLocalByPrefix } from "@utils/Session";
import { prefetchClientsSilently } from "@/prefetch/prefetchClients";
import { prefetchActivityCodeSilently } from "@/prefetch/prefetchActivityCode";
import { prefetchCurrencySilently } from "@/prefetch/prefetchCurrency";
import { prefetchPriceSilently } from "@/prefetch/prefetchPrice";
import { TenantApi } from "@/api/TenantApi";

type LoginFormDataProps = {
  username: string;
  password: string;
} & SignInInput;

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { authStep, handleSignIn: authHandleSignIn, handleNewPassword } = useAuth();

  useEffect(() => {
    if (authStep == "DONE") {
      afterLoginActions()
      navigate('/user/dashboard');
    } else if (authStep == "PAUSED") {
      afterLoginActions()
      navigate('/user/tenant-manage');
    }
  }, [authStep]);

  const afterLoginActions = () => {
    dispatch(login());
    loadSuscriptionData()
    resetCache()
    prefetches()
    setIsLoading(false);
  }

  const loadSuscriptionData = async () => {
    const profileData = await fetchSuscription();
    dispatch(setSuscriptionData(profileData))
    try {
      const tenantData = await TenantApi.fetchTenantBySuscriptionId(`${profileData.id}`)
      dispatch(setTenantData(tenantData))
      try {
        if (!tenantData.suscriptionTypeId) {
          dispatch(setPlanFeatures(null))
          dispatch(setPlanDocumentLimits(null))
        } else {
          const suscriptionType = await SuscriptionTypeApi.get(tenantData.suscriptionTypeId)
          dispatch(setPlanFeatures(parsePlanFeatures(suscriptionType?.features)))
          dispatch(setPlanDocumentLimits(parseDocumentLimits(suscriptionType?.documentLimits)))
        }
      } catch (err) {
        console.error("Unable to load SuscriptionType features", err)
        dispatch(setPlanFeatures(null))
        dispatch(setPlanDocumentLimits(null))
      }
    } catch (err) {
      console.error("Unable to load TenantData", err)
      dispatch(setPlanFeatures(null))
      dispatch(setPlanDocumentLimits(null))
    }
  }

  const prefetches = () => {
    prefetchClientsSilently()
    prefetchActivityCodeSilently()
    prefetchCurrencySilently()
    prefetchPriceSilently()
  }

  const resetCache = () => {
    ClientApi.clearCache()
    CurrencyApi.clearCache()
    PriceApi.clearCache()
    SalemanApi.clearCache()
    ActivityCodeApi.clearCache()
    BankAccountApi.clearCache()
    CustomFieldApi.clearCache()
    InvoiceTemplateApi.clearCache()
    ZoneApi.clearCache()
    CategoryApi.clearCache()
    clearLocalByPrefix("modelsCache/v1")
  }

  const handleSignIn = async (formData: LoginFormDataProps) => {
    setIsLoading(true);
    if (authStep === 'NEW_PASSWORD_REQUIRED') {
      const { password } = formData;
      await handleNewPassword(password);
    } else {
      await authHandleSignIn(formData);
    }
    setIsLoading(false);
  }

  return (
    <div className={styles.loginForm}>
      {(authStep === 'SIGN_IN' || authStep === 'BLOCKED') && (
        <DynamicForm<LoginFormDataProps>
          id="login"
          formConfig={LoginFormConfig}
          isLoading={isLoading}
          onSubmit={handleSignIn} >
        </DynamicForm>
      )}

      {authStep === 'NEW_PASSWORD_REQUIRED' && (
        <>
          <Container>
            <p>Es necesario actualizar su contraseña antes de continuar</p>
          </Container>
          <DynamicForm<LoginFormDataProps>
            id="login"
            formConfig={NewPasswordFormConfig}
            isLoading={isLoading}
            onSubmit={handleSignIn} >
          </DynamicForm>
        </>
      )}
    </div>
  )
}

export default LoginForm;